"use client";

import { useState } from "react";
import Image from "next/image";
import { Icon } from "@to/ui";

/**
 * Task 34.8 · spec §8.4 — the photo gallery for the public property-detail page
 * (`/properties/[key]`). Client component: it owns the active-photo state and the
 * prev/next navigation.
 *
 * Property photos are served from the Bridge CloudFront CDN
 * (`dvvjkgh94f2v6.cloudfront.net`), the ONE listing-media host whitelisted in
 * `next.config.ts` `images.remotePatterns` (Task 34.2) — so these are rendered
 * with `next/image` (unlike the agent avatar / Core logos, whose hosts are NOT
 * uniformly whitelisted and therefore render `unoptimized`). The MAIN photo is
 * the page's LCP and carries `priority`; thumbnails stay lazy. When `photos`
 * is empty the gallery degrades to a tokenized placeholder block (Icon
 * `photo`) — never a broken image.
 *
 * Theme tokens only: layout via Tailwind, every color/shadow via `--brand-*`
 * inline styles. No hardcoded hex.
 */
export function PropertyGallery({
  photos,
  alt,
}: {
  photos: string[];
  /** Accessible label for each photo (the property address, when known). */
  alt: string;
}) {
  const [active, setActive] = useState(0);

  // Empty state — no Bridge media on this listing. A tokenized placeholder, not
  // a broken/empty image element.
  if (photos.length === 0) {
    return (
      <div
        className="flex aspect-[16/10] w-full items-center justify-center overflow-hidden rounded-lg sm:aspect-[2/1]"
        style={{
          backgroundColor: "var(--brand-surface-subtle)",
          borderRadius: "var(--radius-card, 8px)",
        }}
        data-testid="property-gallery-empty"
      >
        <Icon name="photo" size={56} style={{ color: "var(--brand-text-muted)" }} />
      </div>
    );
  }

  const current = Math.min(active, photos.length - 1);
  const go = (delta: number) =>
    setActive((i) => (i + delta + photos.length) % photos.length);

  return (
    <div className="flex flex-col gap-3">
      {/* Main photo — 2:1 on desktop (the v1 hero ratio); 16:10 on mobile. */}
      <div
        className="relative aspect-[16/10] w-full overflow-hidden rounded-lg sm:aspect-[2/1]"
        style={{
          backgroundColor: "var(--brand-surface-subtle)",
          borderRadius: "var(--radius-card, 8px)",
        }}
      >
        <Image
          key={photos[current]}
          src={photos[current]!}
          alt={alt}
          fill
          sizes="(max-width: 1024px) 100vw, 800px"
          className="object-cover"
          priority
        />

        {photos.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous photo"
              className="absolute left-3 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full"
              style={{
                backgroundColor: "var(--brand-card-bg)",
                color: "var(--brand-text)",
                boxShadow: "var(--shadow-resting)",
              }}
            >
              <Icon name="chevron_left" size={22} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next photo"
              className="absolute right-3 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full"
              style={{
                backgroundColor: "var(--brand-card-bg)",
                color: "var(--brand-text)",
                boxShadow: "var(--shadow-resting)",
              }}
            >
              <Icon name="chevron_right" size={22} />
            </button>
            <span
              className="absolute bottom-3 right-3 rounded-full px-2.5 py-1"
              style={{
                backgroundColor: "var(--brand-card-bg)",
                color: "var(--brand-text-muted)",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              {current + 1} / {photos.length}
            </span>
          </>
        )}
      </div>

      {/* Thumbnail strip (only when there's more than one photo). */}
      {photos.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {photos.map((photo, index) => (
            <button
              key={photo}
              type="button"
              onClick={() => setActive(index)}
              aria-label={`View photo ${index + 1}`}
              aria-current={index === current}
              className="relative h-16 w-24 flex-shrink-0 overflow-hidden rounded-md"
              style={{
                borderRadius: "var(--radius-button, 8px)",
                outline:
                  index === current
                    ? "2px solid var(--brand-primary)"
                    : "1px solid var(--brand-card-border)",
                outlineOffset: "-1px",
              }}
            >
              <Image
                src={photo}
                alt=""
                fill
                sizes="96px"
                className="object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
